import { useFacilityContext } from '@aspendental/shared-components-web';
import { Stack } from '@mui/material';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Wizard } from 'react-use-wizard';
import Confirmation from '@src/components/Confirmation';
import PatientInformationStep from '@src/components/PatientInformationStep';
import PromptToCallModal from '@src/components/PromptToCallModal';
import QuestionnaireStep from '@src/components/QuestionnaireStep';
import ScheduleInfoModal from '@src/components/ScheduleInfoModal/ScheduleInfoModal';
import TimeSlotSelectionStep from '@src/components/TimeSlotSelectionStep';
import WizardFooter from '@src/components/WizardFooter/WizardFooter';
import { FormIdMappings } from '@src/constants';
import { useMarketingSourceUrlParse } from '@src/hooks/useMarketingSourceUrlParse';
import { useAppointmentInformationStore, useAppStore, usePatientInformationStore, useScheduleQuestionsStore, useStepManagerStore } from '@src/store';
export interface IScheduleLayoutProps {
  smartyKey: string;
  baseUrl: string;
  enablePtcSlotFilter: boolean;
}
function ScheduleLayout({
  smartyKey,
  baseUrl,
  enablePtcSlotFilter
}: Readonly<IScheduleLayoutProps>) {
  const facilityDetails = useFacilityContext();
  useMarketingSourceUrlParse();
  const {
    setSpecifiedStep,
    setIsTransitioning
  } = useStepManagerStore(store => store);
  const {
    leadId
  } = usePatientInformationStore();
  const [modalType, setModalType] = useState<'promptToCall' | 'existingConsultation'>('promptToCall');
  const {
    schedulePopupError,
    setSchedulePopupError,
    scheduleInfoModal,
    setScheduleInfoModal
  } = useScheduleQuestionsStore(store => store);
  const {
    setLeadFormId
  } = useAppStore(store => store);
  const pathname = usePathname();
  const isPageTwo = pathname === '/2/';
  const formIdToUse = isPageTwo ? FormIdMappings.formID3DentalQuestions : FormIdMappings.formID;
  const {
    presetCenterID,
    presetCenterName,
    setPresetCenterID,
    setPresetCenterName
  } = useAppointmentInformationStore();
  useEffect(() => {
    // keep initial facility in state for tracking purposes
    if (facilityDetails.officeInfo && !presetCenterName && !presetCenterID) {
      setPresetCenterID(facilityDetails.officeInfo.code);
      setPresetCenterName(facilityDetails.officeInfo.name);
    }
  }, [facilityDetails, setPresetCenterID, setPresetCenterName, presetCenterName, presetCenterID]);
  useEffect(() => {
    setLeadFormId(formIdToUse);
  }, [formIdToUse, setLeadFormId]);
  return <>
			<PromptToCallModal open={schedulePopupError} handleClose={() => {
      setSchedulePopupError(false);
      setIsTransitioning(false);
    }} modalType={modalType} data-sentry-element="PromptToCallModal" data-sentry-source-file="ScheduleLayout.tsx" />
			<ScheduleInfoModal open={!!scheduleInfoModal} handleClose={() => {
      setScheduleInfoModal(null);
      setIsTransitioning(false);
    }} {...scheduleInfoModal} data-sentry-element="ScheduleInfoModal" data-sentry-source-file="ScheduleLayout.tsx" />
			<Stack bgcolor="backgrounds.neutral" data-sentry-element="Stack" data-sentry-source-file="ScheduleLayout.tsx">
				<Wizard onStepChange={setSpecifiedStep} footer={<WizardFooter baseUrl={baseUrl} />} data-sentry-element="Wizard" data-sentry-source-file="ScheduleLayout.tsx">
					<QuestionnaireStep data-sentry-element="QuestionnaireStep" data-sentry-source-file="ScheduleLayout.tsx" />
					<PatientInformationStep smartyKey={smartyKey} setModalType={setModalType} data-sentry-element="PatientInformationStep" data-sentry-source-file="ScheduleLayout.tsx" />
					<TimeSlotSelectionStep leadId={leadId} setModalType={setModalType} enablePtcSlotFilter={enablePtcSlotFilter} data-sentry-element="TimeSlotSelectionStep" data-sentry-source-file="ScheduleLayout.tsx" />
					<Confirmation enablePtcSlotFilter={enablePtcSlotFilter} data-sentry-element="Confirmation" data-sentry-source-file="ScheduleLayout.tsx" />
				</Wizard>
			</Stack>
		</>;
}
export default ScheduleLayout;